/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useState } from 'react';
import { settingsApi } from '../../api';
import { RowMenu, SettingGroup, SettingRow } from '../../components/SettingGroups';

import { useLoader } from '../../components/common';
import { SettingsPage, Panel } from './shell';
import BedDialog from './BedDialog';
import RoomDialog from './RoomDialog';

export function SettingRoom() {
  const rooms = useLoader(() => settingsApi.rooms(), []);
  const [creating, setCreating] = useState(null);   // { kind, roomId } while open
  const [editing, setEditing] = useState(null);     // { kind, row, roomId }

  /* `roomId` is passed in rather than read off the bed: a bed arrives inside the
     room that holds it and carries no room of its own. */
  const bedRow = (b, roomId, reload) => (
    <SettingRow
      key={b.id}
      /* 30px, measured: their bed row's grip is a 30x30 glyph, and it — not the
         24px label — is what makes the row 58px rather than 52. */
      gripSize={30}
      label={(
        <button
          type="button"
          className="row-open"
          onClick={() => setEditing({ kind: 'bed', row: b, roomId })}
        >
          <span data-no-translate>{b.name || `เตียง ${b.code}`}</span>
        </button>
      )}
      trailing={<span className="badge badge-grey">{b.capacity} ที่</span>}
      menu={(
        <RowMenu
          items={[
            { label: 'แก้ไข', onClick: () => setEditing({ kind: 'bed', row: b, roomId }) },
            { label: 'ลบ', danger: true,
              onClick: async () => { await settingsApi.remove('beds', b.id); reload(); } },
          ]}
        />
      )}
    />
  );

  return (
    <SettingsPage
      action="สร้างห้อง"
      actionMenu={[
        { label: 'เตียง', onClick: () => setCreating({ kind: 'bed' }) },
        { label: 'ห้องนวด', onClick: () => setCreating({ kind: 'room' }) },
      ]}
    >
      <Panel loader={rooms}>
        {(d) => (
          <>
            {d.rooms.map((r) => (
              <SettingGroup
                key={r.id}
                title={<span data-no-translate>{r.name_th || r.code}</span>}
                count={r.beds.length}
                countUnit="เตียง"
                empty="ยังไม่มีเตียงในห้องนี้"
                menu={(
                  <RowMenu
                    items={[
                      { label: 'แก้ไขห้อง', onClick: () => setEditing({ kind: 'room', row: r }) },
                      { label: 'เพิ่มเตียง',
                        onClick: () => setCreating({ kind: 'bed', roomId: r.id }) },
                      { label: 'ลบห้อง', danger: true,
                        onClick: async () => { await settingsApi.remove('rooms', r.id); rooms.reload(); } },
                    ]}
                  />
                )}
              >
                {r.beds.map((b) => bedRow(b, r.id, rooms.reload))}
              </SettingGroup>
            ))}

            {d.unassignedBeds.length > 0 && (
              <SettingGroup
                title="เตียงที่ยังไม่ระบุห้อง"
                count={d.unassignedBeds.length}
                countUnit="เตียง"
              >
                {d.unassignedBeds.map((b) => bedRow(b, '', rooms.reload))}
              </SettingGroup>
            )}
          </>
        )}
      </Panel>

      {creating?.kind === 'room' && (
        <RoomDialog
          onSubmit={async (v) => {
            await settingsApi.createRoom({
              code: v.code,
              nameTh: v.nameTh,
              isPrivate: v.isPrivate,
              isActive: v.isActive,
            });
            rooms.reload();
            setCreating(null);
          }}
          onClose={() => setCreating(null)}
        />
      )}

      {editing?.kind === 'room' && (
        <RoomDialog
          room={editing.row}
          onSubmit={async (v) => {
            await settingsApi.update('rooms', editing.row.id, {
              code: v.code,
              nameTh: v.nameTh,
              isPrivate: v.isPrivate,
              isActive: v.isActive,
            });
            rooms.reload();
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {(creating?.kind === 'bed' || editing?.kind === 'bed') && (
        <BedDialog
          bed={editing?.row}
          roomId={editing ? editing.roomId : creating.roomId}
          rooms={rooms.data?.rooms || []}
          onSubmit={async (payload) => {
            if (editing) await settingsApi.update('beds', editing.row.id, payload);
            else await settingsApi.createBed(payload);
            rooms.reload();
            setCreating(null);
            setEditing(null);
          }}
          onClose={() => { setCreating(null); setEditing(null); }}
        />
      )}
    </SettingsPage>
  );
}
